import { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Upload, Trash2, ArrowUp, ArrowDown, Loader2, ImagePlus, Star } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const VEHICLE_TYPES = ['Prime Mover', 'Rigid', 'Curtainsider', 'Tipper', 'Crane Truck', 'Refrigerated', 'Flatbed', 'Trailer', 'Other'];
const TRANSMISSIONS = ['Automatic', 'Manual', 'AMT', 'None'];

const EMPTY = {
  title: '',
  vehicle_type: 'Prime Mover',
  make: '',
  model: '',
  year: new Date().getFullYear(),
  price: '',
  poa: false,
  kilometres: '',
  transmission: 'Automatic',
  registration: '',
  stock_number: '',
  description: '',
  featured: false,
  status: 'Available',
};

// Client-side resize to keep base64 payloads small
const resizeImage = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1100;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          const ratio = Math.min(MAX / width, MAX / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export default function VehicleForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const dragIdx = useRef(null);

  const [form, setForm] = useState(EMPTY);
  const [images, setImages] = useState([]); // saved images (edit) - {id, image_data}
  const [pending, setPending] = useState([]); // new base64 strings not yet uploaded (create)
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    api
      .get(`/admin/vehicles/${id}`)
      .then((res) => {
        const v = res.data;
        setForm({
          title: v.title,
          vehicle_type: v.vehicle_type,
          make: v.make,
          model: v.model,
          year: v.year,
          price: v.price,
          poa: v.poa || false,
          kilometres: v.kilometres,
          transmission: v.transmission || 'None',
          registration: v.registration || '',
          stock_number: v.stock_number || '',
          description: v.description || '',
          featured: v.featured,
          status: v.status,
        });
        setImages(v.images || []);
      })
      .catch(() => {
        toast.error('Vehicle not found');
        navigate('/admin/vehicles');
      })
      .finally(() => setLoading(false));
  }, [id, isEdit, navigate]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target?.value ?? e }));

  const processFiles = async (files) => {
    const imageFiles = [...files].filter((f) => f.type.startsWith('image/'));
    if (imageFiles.length === 0) return;
    setUploading(true);
    try {
      const resized = await Promise.all(imageFiles.map(resizeImage));
      if (isEdit) {
        const res = await api.post(`/admin/vehicles/${id}/images`, { images: resized });
        setImages((prev) => [...prev, ...res.data.images]);
        toast.success(`${resized.length} image${resized.length > 1 ? 's' : ''} uploaded`);
      } else {
        setPending((prev) => [...prev, ...resized]);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process images');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    processFiles(e.dataTransfer.files);
  };

  // --- saved image management (edit mode) ---
  const deleteImage = async (imageId) => {
    try {
      await api.delete(`/admin/images/${imageId}`);
      setImages((prev) => prev.filter((img) => img.id !== imageId));
      toast.success('Image removed');
    } catch {
      toast.error('Failed to remove image');
    }
  };

  const persistOrder = async (list) => {
    setImages(list);
    try {
      await api.put(`/admin/vehicles/${id}/images/reorder`, { image_ids: list.map((i) => i.id) });
    } catch {
      toast.error('Failed to save order');
    }
  };

  const moveImage = (idx, dir) => {
    const list = [...images];
    const target = idx + dir;
    if (target < 0 || target >= list.length) return;
    [list[idx], list[target]] = [list[target], list[idx]];
    persistOrder(list);
  };

  const handleImgDragStart = (idx) => () => {
    dragIdx.current = idx;
  };
  const handleImgDrop = (idx) => (e) => {
    e.preventDefault();
    const from = dragIdx.current;
    if (from === null || from === idx) return;
    const list = [...images];
    const [moved] = list.splice(from, 1);
    list.splice(idx, 0, moved);
    dragIdx.current = null;
    persistOrder(list);
  };

  // --- pending image management (create mode) ---
  const movePending = (idx, dir) => {
    const list = [...pending];
    const target = idx + dir;
    if (target < 0 || target >= list.length) return;
    [list[idx], list[target]] = [list[target], list[idx]];
    setPending(list);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.make.trim() || !form.model.trim()) {
      toast.error('Title, make and model are required');
      return;
    }
    if ((form.price === '' && !form.poa) || form.kilometres === '') {
      toast.error(form.poa ? 'Kilometres are required' : 'Price and kilometres are required');
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      transmission: form.transmission === 'None' ? '' : form.transmission,
      year: parseInt(form.year, 10),
      price: form.poa ? 0 : parseFloat(form.price),
      kilometres: parseInt(form.kilometres, 10),
    };
    try {
      if (isEdit) {
        await api.put(`/admin/vehicles/${id}`, payload);
        toast.success('Vehicle updated');
      } else {
        const res = await api.post('/admin/vehicles', payload);
        if (pending.length > 0) {
          await api.post(`/admin/vehicles/${res.data.id}/images`, { images: pending });
        }
        toast.success('Vehicle created');
      }
      navigate('/admin/vehicles');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save vehicle');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 max-w-4xl">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  const displayImages = isEdit ? images : pending.map((data, i) => ({ id: `pending-${i}`, image_data: data }));

  return (
    <div className="max-w-4xl">
      <Link
        to="/admin/vehicles"
        className="inline-flex items-center gap-2 text-sm text-[#53565A] hover:text-[#24135F] transition-colors duration-150 mb-4"
      >
        <ArrowLeft className="h-4 w-4" /> Back to vehicles
      </Link>
      <h1 className="font-heading text-3xl tracking-wide text-[#24135F] mb-6">
        {isEdit ? 'EDIT VEHICLE' : 'ADD VEHICLE'}
      </h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white border border-[#D9D9D6] rounded-xl p-6 space-y-5">
          <h2 className="font-heading text-xl tracking-wider text-[#24135F]">VEHICLE DETAILS</h2>
          <div>
            <Label className="text-xs font-semibold text-[#53565A]">
              Title <span className="text-[#E4002B]">*</span>
            </Label>
            <Input
              data-testid="vehicle-form-title-input"
              value={form.title}
              onChange={set('title')}
              placeholder="e.g. 2021 Volvo FH540 Prime Mover"
              className="mt-1.5 h-11 rounded-lg focus-visible:ring-2 focus-visible:ring-[#E4002B]"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold text-[#53565A]">
                Make <span className="text-[#E4002B]">*</span>
              </Label>
              <Input
                data-testid="vehicle-form-make-input"
                value={form.make}
                onChange={set('make')}
                placeholder="Volvo"
                className="mt-1.5 h-11 rounded-lg focus-visible:ring-2 focus-visible:ring-[#E4002B]"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#53565A]">
                Model <span className="text-[#E4002B]">*</span>
              </Label>
              <Input
                data-testid="vehicle-form-model-input"
                value={form.model}
                onChange={set('model')}
                placeholder="FH540"
                className="mt-1.5 h-11 rounded-lg focus-visible:ring-2 focus-visible:ring-[#E4002B]"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#53565A]">
                Year <span className="text-[#E4002B]">*</span>
              </Label>
              <Input
                data-testid="vehicle-form-year-input"
                type="number"
                min="1950"
                max="2035"
                value={form.year}
                onChange={set('year')}
                className="mt-1.5 h-11 rounded-lg focus-visible:ring-2 focus-visible:ring-[#E4002B]"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#53565A]">Vehicle Type</Label>
              <Select value={form.vehicle_type} onValueChange={(v) => setForm((f) => ({ ...f, vehicle_type: v }))}>
                <SelectTrigger data-testid="vehicle-form-type-select" className="mt-1.5 h-11 rounded-lg">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VEHICLE_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#53565A]">
                Price (AUD) {!form.poa && <span className="text-[#E4002B]">*</span>}
              </Label>
              <Input
                data-testid="vehicle-form-price-input"
                type="number"
                min="0"
                step="1"
                value={form.poa ? '' : form.price}
                onChange={set('price')}
                placeholder={form.poa ? 'POA - no price shown' : '189000'}
                disabled={form.poa}
                className="mt-1.5 h-11 rounded-lg focus-visible:ring-2 focus-visible:ring-[#E4002B] disabled:opacity-60"
              />
              <div className="flex items-center gap-2 mt-2">
                <Switch
                  data-testid="vehicle-form-poa-switch"
                  checked={form.poa}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, poa: v }))}
                />
                <span className="text-xs text-[#53565A]">
                  <strong className="text-[#24135F]">POA</strong> - hide price, buyers enquire / make an offer
                </span>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#53565A]">
                Kilometres <span className="text-[#E4002B]">*</span>
              </Label>
              <Input
                data-testid="vehicle-form-kilometres-input"
                type="number"
                min="0"
                value={form.kilometres}
                onChange={set('kilometres')}
                placeholder="455000"
                className="mt-1.5 h-11 rounded-lg focus-visible:ring-2 focus-visible:ring-[#E4002B]"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#53565A]">Transmission</Label>
              <Select value={form.transmission} onValueChange={(v) => setForm((f) => ({ ...f, transmission: v }))}>
                <SelectTrigger data-testid="vehicle-form-transmission-select" className="mt-1.5 h-11 rounded-lg">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TRANSMISSIONS.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#53565A]">Registration</Label>
              <Input
                data-testid="vehicle-form-registration-input"
                value={form.registration}
                onChange={set('registration')}
                placeholder="XQ23AB"
                className="mt-1.5 h-11 rounded-lg focus-visible:ring-2 focus-visible:ring-[#E4002B]"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#53565A]">Stock Number</Label>
              <Input
                data-testid="vehicle-form-stock-input"
                value={form.stock_number}
                onChange={set('stock_number')}
                placeholder="HTS-1024"
                className="mt-1.5 h-11 rounded-lg focus-visible:ring-2 focus-visible:ring-[#E4002B]"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#53565A]">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="vehicle-form-status-select" className="mt-1.5 h-11 rounded-lg">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Available">Available</SelectItem>
                  <SelectItem value="Sold">Sold</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold text-[#53565A]">Description</Label>
            <Textarea
              data-testid="vehicle-form-description-textarea"
              value={form.description}
              onChange={set('description')}
              rows={7}
              placeholder="Full vehicle description, features, service history..."
              className="mt-1.5 rounded-lg focus-visible:ring-2 focus-visible:ring-[#E4002B]"
            />
          </div>
          <div className="flex items-center gap-3 pt-1">
            <Switch
              id="featured-switch"
              data-testid="vehicle-form-featured-switch"
              checked={form.featured}
              onCheckedChange={(v) => setForm((f) => ({ ...f, featured: v }))}
              className="data-[state=checked]:bg-[#E4002B]"
            />
            <Label htmlFor="featured-switch" className="text-sm flex items-center gap-2 cursor-pointer">
              <Star className="h-4 w-4 text-[#E4002B]" /> Feature this vehicle on the homepage
            </Label>
          </div>
        </div>

        {/* Images */}
        <div className="bg-white border border-[#D9D9D6] rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl tracking-wider text-[#24135F]">PHOTOS</h2>
            <span className="text-xs text-[#53565A]/70">{displayImages.length}/15 images</span>
          </div>
          <div
            data-testid="vehicle-form-image-dropzone"
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors duration-150 ${
              dragOver ? 'border-[#E4002B] bg-[#E4002B]/5' : 'border-[#D9D9D6] hover:border-[#53565A]/50 bg-[#D9D9D6]/15'
            }`}
          >
            {uploading ? (
              <Loader2 className="h-8 w-8 text-[#E4002B] animate-spin mx-auto" />
            ) : (
              <ImagePlus className="h-8 w-8 text-[#53565A]/70 mx-auto" />
            )}
            <p className="text-sm font-medium text-[#53565A] mt-3">
              {uploading ? 'Processing images...' : 'Drag & drop photos here, or click to browse'}
            </p>
            <p className="text-xs text-[#53565A]/70 mt-1">JPG or PNG - photos are optimised automatically</p>
            <input
              ref={fileInputRef}
              data-testid="vehicle-form-image-input"
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                processFiles(e.target.files);
                e.target.value = '';
              }}
            />
          </div>

          {displayImages.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="vehicle-form-image-grid">
              {displayImages.map((img, idx) => (
                <div
                  key={img.id}
                  draggable={isEdit}
                  onDragStart={isEdit ? handleImgDragStart(idx) : undefined}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={isEdit ? handleImgDrop(idx) : undefined}
                  className="relative group rounded-lg overflow-hidden border border-[#D9D9D6] aspect-[4/3] bg-[#D9D9D6]/30"
                >
                  <img src={img.image_data} alt={`Vehicle ${idx + 1}`} className="w-full h-full object-cover" />
                  {idx === 0 && (
                    <span className="absolute top-1.5 left-1.5 bg-[#24135F]/80 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">
                      MAIN
                    </span>
                  )}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-1.5 flex justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                    <button
                      type="button"
                      data-testid="image-move-up-button"
                      onClick={() => (isEdit ? moveImage(idx, -1) : movePending(idx, -1))}
                      disabled={idx === 0}
                      className="p-1.5 bg-white/90 rounded-md hover:bg-white disabled:opacity-40 transition-colors duration-150"
                      title="Move earlier"
                    >
                      <ArrowUp className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      data-testid="image-move-down-button"
                      onClick={() => (isEdit ? moveImage(idx, 1) : movePending(idx, 1))}
                      disabled={idx === displayImages.length - 1}
                      className="p-1.5 bg-white/90 rounded-md hover:bg-white disabled:opacity-40 transition-colors duration-150"
                      title="Move later"
                    >
                      <ArrowDown className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      data-testid="image-delete-button"
                      onClick={() =>
                        isEdit ? deleteImage(img.id) : setPending((prev) => prev.filter((_, i) => i !== idx))
                      }
                      className="p-1.5 bg-white/90 rounded-md hover:bg-[#E4002B]/10 transition-colors duration-150"
                      title="Remove"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-[#E4002B]" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {isEdit && displayImages.length > 1 && (
            <p className="text-xs text-[#53565A]/70">Drag photos to reorder, or use the arrows. The first photo is the main listing image.</p>
          )}
        </div>

        <div className="flex gap-3">
          <Button
            type="submit"
            disabled={saving || uploading}
            data-testid="vehicle-form-save-button"
            className="rounded-full bg-[#E4002B] text-white hover:bg-[#E4002B]/90 h-12 px-8 font-bold tracking-wider transition-colors duration-150"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {saving ? 'SAVING...' : isEdit ? 'SAVE CHANGES' : 'CREATE VEHICLE'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/admin/vehicles')}
            className="rounded-full border-[#D9D9D6] h-12 px-8 font-bold tracking-wider"
          >
            CANCEL
          </Button>
        </div>
      </form>
    </div>
  );
}
